"use strict";
/**
 * The limits of reading in the Bridge and the App runtime, the same on the
 * platform and in the StreamKit: rows and windows of a read, the tables of a
 * page, the queue and caches of the page, the busy answer. This module
 * imports nothing, so the App runtime bundle stays free of the App contract
 * and its schemas; app-base-contract.ts, app-base-source-query-schema.ts and
 * app-base-source-query.ts hand the same values on.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_BASE_SOURCE_QUERY_TRANSPORT_FIELDS = exports.APP_BASE_BRIDGE_BUDGETS = exports.APP_BASE_COMPLETE_READ_WINDOWS = exports.APP_BASE_READ_BUDGETS = exports.APP_PAGE_TABLE_LIMIT = exports.APP_BASE_SOURCE_QUERY_DEFAULT_LIMIT = exports.APP_BASE_SOURCE_QUERY_MAX_WINDOWS = exports.APP_BASE_LINKED_DEPTH = exports.APP_BASE_MAX_DATASET_ROWS = void 0;
exports.appBaseDataBusyBody = appBaseDataBusyBody;
/** The rows one query reads at most (APP_BASE_BUDGETS.maxDatasetRows). */
exports.APP_BASE_MAX_DATASET_ROWS = 5000;
/** Relations one `linkedFrom` reads across: its own and those of the
 * origins it names in turn. */
exports.APP_BASE_LINKED_DEPTH = 3;
/** Further windows one held read asks with its first one. */
exports.APP_BASE_SOURCE_QUERY_MAX_WINDOWS = 128;
/** The rows of a window whose query names no limit. */
exports.APP_BASE_SOURCE_QUERY_DEFAULT_LIMIT = 100;
/**
 * Tables one page reads with its frame, and tables one Agent Action binds:
 * they drive permission checks, live subscriptions and one dataset batch.
 * Read through APP_BASE_BUDGETS.maxPageTables; defined here so schemas above
 * the budgets can use it.
 */
exports.APP_PAGE_TABLE_LIMIT = 32;
/** The read budgets of APP_BASE_BUDGETS, which takes them as they are. */
exports.APP_BASE_READ_BUDGETS = {
    /**
     * Bounds permission checks, live query/watch subscriptions and one dataset
     * batch of a page: the tables its route and the frame read. An Agent Action
     * binds as many.
     */
    maxPageTables: exports.APP_PAGE_TABLE_LIMIT,
    /** Bounds query working sets and response memory even with a larger binding budget. */
    maxDatasetRows: exports.APP_BASE_MAX_DATASET_ROWS,
    defaultDatasetRows: 500,
};
/**
 * Further windows the first request of a complete read asks along: the
 * windows of `defaultDatasetRows` after the first up to the rows one query
 * reads. A complete read that holds nothing yet asks no more, whatever the
 * size of its windows.
 */
exports.APP_BASE_COMPLETE_READ_WINDOWS = Math.ceil(exports.APP_BASE_MAX_DATASET_ROWS / exports.APP_BASE_READ_BUDGETS.defaultDatasetRows) - 1;
/**
 * How the Bridge reads on every page, in the StreamKit and on the platform
 * alike: a full read walks windows of `APP_BASE_READ_BUDGETS.defaultDatasetRows`.
 */
exports.APP_BASE_BRIDGE_BUDGETS = {
    /** Reads one page runs at once; more wait in the queue. */
    parallelReads: 4,
    /** Reads waiting at once; one more is answered busy (queue_full). The
     * further windows of a read that began wait ahead and do not count. */
    queuedReads: 64,
    /** A read waiting while no read ended for this long is answered busy. */
    queueWaitMs: 10_000,
    /** Complete reads a page keeps to read only what changed since (initial
     * setting, not a measurement); the oldest leave first beyond it. */
    heldReadBytes: 16 * 1024 * 1024,
    /** Bytes of the address of one read, its query included: well below the
     * 8 KiB a proxy admits for a request line. A larger query is refused
     * before it is sent (APP_BASE_QUERY_TOO_LARGE); rows across a relation
     * are read with `linkedFrom`. */
    addressBytes: 6000,
    /** Bytes of the query windows a page keeps to revalidate with their ETag. */
    cachedWindowBytes: 8 * 1024 * 1024,
    /** Query windows a page keeps; the oldest leave first beyond it. */
    cachedWindows: 128,
    /** Routes whose change signals a page follows at once. */
    activeRoutes: 16,
    /** Attempts of one read that change signals overtake; the last ends on the
     * state the server holds for it. */
    readAttempts: 3,
    /** Transport fragments of one window. */
    transportFragments: 4096,
    /** Rows a write names for one source as pending. */
    writeRowsPerSource: 500,
    /** The longest undo window of a write. */
    writeUndoMs: 60_000,
};
/**
 * The 503 answer of a read of App data that found its workspace busy, the
 * same on the platform and in the StreamKit, so that the Bridge retries both
 * alike.
 */
function appBaseDataBusyBody(reason) {
    return {
        code: 'APP_BASE_DATA_BUSY',
        reason,
        retryable: true,
        retryAfterSeconds: 1,
        message: reason === 'queue_full'
            ? 'App data is busy for this workspace; retry shortly'
            : 'App data did not become available in time; retry shortly',
    };
}
/** Fields the Bridge validates and caches but removes before resolving. */
exports.APP_BASE_SOURCE_QUERY_TRANSPORT_FIELDS = [
    'revision',
    'revisionVector',
    // The state a host keeps for the further windows of a read; see
    // createAppBaseHeldValues. Hosts that keep none send none.
    'pin',
    // Delta reads; see createAppBaseSourceQueryDeltas.
    'order',
    'sameOrder',
    'deltaToken',
    // Further windows of a held read; see executePortableAppBaseSourceQueryWindows.
    'windows',
];
